/**
 * The platform's own channel credentials — V4 follow-up.
 *
 * The ruling this implements: no credential and no channel switch may ever
 * require editing `.env` or restarting the server. So the platform's Brevo key
 * — previously an environment variable and a deploy — is a form.
 *
 * Write-only, like the workspace integrations screen: a saved key is shown as
 * `••••1234` and replaced rather than edited, because a field that can display
 * a secret is a field that will eventually display it to the wrong person.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, KeyRound, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, api } from '@/lib/api';
import { describeError } from '@/lib/errorMessage';
import { buildSavePayload } from './savePayload';

interface ChannelDescription {
  source: 'workspace' | 'platform' | null;
  delivers: boolean;
  configured: boolean;
  summary: string;
  senderEmail?: string | null;
}

/** Which store answered — mirrors the server's `CredentialSource` (V9.1 §5). */
type CredentialSource = 'platform' | 'env' | 'none' | 'unreadable';

const SOURCE_LABEL: Record<CredentialSource, string> = {
  platform: 'saved here',
  env: 'environment variables',
  none: 'nothing configured',
  unreadable: 'stored but unreadable',
};

interface StorageStatus {
  bucket: string | null;
  configured: boolean;
  credentialSource: CredentialSource;
  summary: string;
}

interface PlatformSettingsView {
  brevo: {
    apiKey: string | null;
    senderEmail: string | null;
    senderName: string | null;
    fromEnvBootstrap: boolean;
  };
  whatsapp: {
    accessToken: string | null;
    phoneNumberId: string | null;
    wabaId: string | null;
    fromEnvBootstrap: boolean;
  };
  s3: {
    bucket: string | null;
    region: string | null;
    endpoint: string | null;
    forcePathStyle: boolean;
    accessKeyId: string | null;
    secretAccessKey: string | null;
    credentialSource: CredentialSource;
    fromEnvBootstrap: boolean;
  };
  push: {
    vapidPublicKey: string | null;
    vapidPrivateKey: string | null;
    contact: string | null;
    configured: boolean;
  };
  status: {
    email: ChannelDescription;
    whatsapp: ChannelDescription;
    storage: StorageStatus;
  };
}

const settingsKey = ['platform-settings'] as const;

function usePlatformSettings() {
  return useQuery({
    queryKey: settingsKey,
    retry: false,
    queryFn: async () => (await api.get<PlatformSettingsView>('/platform/settings')).data,
  });
}

/** A stored secret is replaced, never revealed. */
function SecretRow({
  id,
  label,
  masked,
  value,
  editing,
  onEdit,
  onCancel,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  masked: string | null;
  value: string;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {masked && !editing ? (
        // Says "Stored" in words. A masked value on its own was read as an
        // empty field, and therefore as "my save did not work" — which, given
        // an identical-looking unconfigured state, was a fair reading.
        <div className="flex items-center gap-2" data-testid={`${id}-stored`}>
          <Badge tone="accent">
            <Check className="size-3" /> Stored
          </Badge>
          <span className="vt-mono text-[13px]" data-testid={`${id}-masked`}>
            {masked}
          </span>
          <Button variant="ghost" size="sm" data-testid={`${id}-replace`} onClick={onEdit}>
            Replace
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            id={id}
            data-testid={id}
            type="password"
            autoComplete="off"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
          {masked && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      )}
      {!masked && !editing && (
        <span className="text-[11px] vt-muted" data-testid={`${id}-empty`}>
          Not set — nothing is stored for this field yet.
        </span>
      )}
    </div>
  );
}

function StatusLine({ label, status }: { label: string; status: ChannelDescription }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px]" data-testid={`platform-status-${label.toLowerCase()}`}>
      <span className="font-medium">{label}:</span>
      <Badge tone={status.delivers ? 'accent' : 'warn'}>
        {status.delivers ? (status.source ?? 'configured') : 'not delivering'}
      </Badge>
      {/* Prose, not a flag. "console" read as "working" is what cost an
          evening; this says what will happen in words. */}
      <span className="vt-muted">{status.summary}</span>
    </div>
  );
}

export function PlatformSettingsCard() {
  const { data, isLoading, error } = usePlatformSettings();
  const qc = useQueryClient();

  const [edits, setEdits] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [storageResult, setStorageResult] = useState<{
    steps: { step: string; ok: boolean; detail: string; error?: string }[];
    credentialSource: CredentialSource;
    credentialSummary: string;
  } | null>(null);

  const [waTestNumber, setWaTestNumber] = useState('');
  const [waTestResult, setWaTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  /**
   * V5-06 §5 — a real send from the platform account.
   *
   * Meta's error strings are reported verbatim: "template name does not exist"
   * and "access token expired" need opposite fixes, and paraphrasing them into
   * "WhatsApp failed" is how an afternoon disappears.
   */
  const whatsappTest = useMutation({
    mutationFn: async (to: string) =>
      (
        await api.post<{ sent: boolean; driver: string; templateName: string }>(
          '/platform/settings/whatsapp-test',
          { to },
        )
      ).data,
    onSuccess: (result) => {
      const where = result.driver === 'console' ? 'the outbox (no credentials yet)' : 'WhatsApp';
      setWaTestResult({ ok: true, message: `Sent ${result.templateName} to ${where}.` });
      toast.success('Test WhatsApp sent');
    },
    onError: (err: unknown) => {
      // `describeError`, not `err.message`: a validation failure's envelope
      // message is the useless "Request validation failed", and the sentence
      // that actually tells you the number is in the wrong format lives in
      // `details`. This control exists to explain failures.
      const message = describeError(err, 'WhatsApp test failed');
      setWaTestResult({ ok: false, message });
      toast.error(message, { duration: 15_000 });
    },
  });

  const storageTest = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          ok: boolean;
          bucketLabel: string;
          steps: { step: string; ok: boolean; detail: string; error?: string }[];
          credentialSource: CredentialSource;
          credentialSummary: string;
        }>('/platform/settings/storage/test', {})
      ).data,
    onSuccess: (result) => {
      setStorageResult(result);
      /**
       * The source is named in the toast as well as the panel — V9.1 §5.
       *
       * `SignatureDoesNotMatch` naming a key id you have just saved reads as
       * "the save failed". It is not: it is the saved id signed with a secret
       * from somewhere else. Whichever way the check goes, the answer to
       * "whose keys were these?" is on screen without being asked for.
       */
      const via = `using ${SOURCE_LABEL[result.credentialSource]}`;
      if (result.ok) {
        toast.success(`Storage round trip passed against ${result.bucketLabel} — ${via}`);
      } else {
        const failed = result.steps.find((s) => !s.ok);
        toast.error(`${failed?.step} (${via}): ${failed?.error ?? failed?.detail}`, {
          duration: 15_000,
        });
      }
      void qc.invalidateQueries({ queryKey: settingsKey });
    },
    onError: (err: unknown) =>
      toast.error(describeError(err, 'The storage check could not run'), { duration: 15_000 }),
  });

  const generateKeys = useMutation({
    mutationFn: async () =>
      (await api.post<{ vapidPublicKey: string }>('/platform/settings/push/generate', {})).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKey });
      toast.success('Push keys generated — the private half stays on the server.');
    },
    onError: (err: unknown) => toast.error(describeError(err, 'Could not generate keys')),
  });

  const save = useMutation({
    mutationFn: async () => {
      // Every credential decision lives in `buildSavePayload`, which is pure
      // and tested — see `savePayload.test.ts`. This used to be assembled
      // inline, and the shape it produced was half of the outage V9.1 fixes.
      const payload = buildSavePayload(edits, {
        'brevo.apiKey': Boolean(data?.brevo.apiKey),
        'whatsapp.accessToken': Boolean(data?.whatsapp.accessToken),
        's3.accessKeyId': Boolean(data?.s3.accessKeyId),
        's3.secretAccessKey': Boolean(data?.s3.secretAccessKey),
      });
      return (await api.patch<{ updated: string[] }>('/platform/settings', payload)).data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKey });
      setEdits({});
      setEditing({});
      // The point of the whole exercise, said out loud.
      toast.success('Saved — the next message sends with these credentials. No restart needed.');
    },
    // Verbatim, and for long enough to read: a validation failure names the
    // offending field, and an unreachable API says so rather than looking like
    // a server rejection.
    onError: (err: unknown) =>
      toast.error(describeError(err, 'Could not save the settings'), { duration: 15_000 }),
  });

  // A 401/403 means "not the platform admin", and the console already says so
  // — rendering nothing is right. Anything else (the API down, a 500) must not
  // silently remove the card: an operator who sees no form concludes the
  // feature does not exist, which is exactly how a dead dev server got read as
  // "saving is broken".
  if (error) {
    const denied = error instanceof ApiError && (error.status === 401 || error.status === 403);
    if (denied) return null;
    return (
      <div
        role="alert"
        data-testid="platform-settings-error"
        className="vt-surface flex items-start gap-2 border border-[var(--danger)]/40 p-4 text-[13px] text-[var(--danger)]"
      >
        <AlertTriangle className="mt-0.5 size-4 shrink-0" />
        <div>
          <p className="font-medium">Platform credentials could not be loaded</p>
          <p className="mt-0.5 text-[12px] opacity-90">{describeError(error)}</p>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="vt-surface p-4">
        <Skeleton className="h-40" />
      </div>
    );
  }

  async function sendTest(): Promise<void> {
    setTesting(true);
    try {
      const res = await api.post<{
        sentTo: string;
        credentialSource: string;
        providerMessageId?: string;
      }>(
        '/platform/settings/test',
        { channel: 'email', to: testTo.trim() },
      );
      toast.success(
        `Test sent to ${res.data.sentTo} using the ${res.data.credentialSource} key` +
          ` (provider id ${res.data.providerMessageId ?? 'n/a'})`,
      );
    } catch (err) {
      // 15 seconds: a provider error is usually long and always the point.
      toast.error(describeError(err, 'Test failed'), { duration: 15_000 });
    } finally {
      setTesting(false);
      void qc.invalidateQueries({ queryKey: settingsKey });
    }
  }

  const secretRow = (path: string, label: string, masked: string | null, placeholder?: string) => (
    <SecretRow
      id={path}
      label={label}
      masked={masked}
      placeholder={placeholder}
      value={edits[path] ?? ''}
      editing={Boolean(editing[path])}
      onEdit={() => setEditing((e) => ({ ...e, [path]: true }))}
      onCancel={() => {
        setEditing((e) => ({ ...e, [path]: false }));
        setEdits((e) => {
          const next = { ...e };
          delete next[path];
          return next;
        });
      }}
      onChange={(v) => setEdits((e) => ({ ...e, [path]: v }))}
    />
  );

  return (
    <section className="vt-surface flex flex-col gap-3 p-4" data-testid="platform-settings">
      <div className="flex items-center gap-2">
        <KeyRound className="size-4 vt-muted" />
        <h2 className="flex-1 text-[15px] font-semibold">Platform credentials</h2>
      </div>
      <p className="text-[12px] vt-muted">
        Used for sign-up verification, invitations and password resets, and for any workspace whose
        plan allows the shared sender. Stored encrypted; changes take effect on the next message
        with no restart.
      </p>

      <div className="flex flex-col gap-1 rounded-[8px] border border-[var(--border)] p-2.5">
        <StatusLine label="Email" status={data.status.email} />
        <StatusLine label="WhatsApp" status={data.status.whatsapp} />
      </div>

      {!data.status.email.delivers && (
        <div
          role="status"
          data-testid="platform-email-unconfigured"
          className="flex items-start gap-2 rounded-[8px] border border-[var(--warn)]/40 bg-[var(--warn)]/10 px-3 py-2.5 text-[12px] text-[var(--warn)]"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <span>
            No platform email key. Sign-up verification, invitations and password resets are being
            written to the local outbox instead of delivered.
          </span>
        </div>
      )}

      {data.brevo.fromEnvBootstrap && (
        <p className="text-[11px] vt-muted" data-testid="brevo-from-env">
          Currently using the <code>BREVO_API_KEY</code> environment variable. Saving a key here
          replaces it, and removes the need for it.
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {secretRow('brevo.apiKey', 'Brevo API key', data.brevo.apiKey, 'xkeysib-…')}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="brevo.senderEmail">Sender email</Label>
          <Input
            id="brevo.senderEmail"
            data-testid="platform-sender-email"
            placeholder={data.brevo.senderEmail ?? 'tasks@example.com'}
            value={edits['brevo.senderEmail'] ?? data.brevo.senderEmail ?? ''}
            onChange={(e) => setEdits((s) => ({ ...s, 'brevo.senderEmail': e.target.value }))}
          />
          <span className="text-[11px] vt-muted">Must be a verified sender in Brevo.</span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="brevo.senderName">Sender name</Label>
          <Input
            id="brevo.senderName"
            data-testid="platform-sender-name"
            placeholder={data.brevo.senderName ?? 'VirtueTasks'}
            value={edits['brevo.senderName'] ?? data.brevo.senderName ?? ''}
            onChange={(e) => setEdits((s) => ({ ...s, 'brevo.senderName': e.target.value }))}
          />
        </div>
        {secretRow('whatsapp.accessToken', 'WhatsApp access token', data.whatsapp.accessToken)}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="whatsapp.phoneNumberId">WhatsApp phone number ID</Label>
          <Input
            id="whatsapp.phoneNumberId"
            data-testid="platform-wa-phone"
            value={edits['whatsapp.phoneNumberId'] ?? data.whatsapp.phoneNumberId ?? ''}
            onChange={(e) => setEdits((s) => ({ ...s, 'whatsapp.phoneNumberId': e.target.value }))}
          />
        </div>
      </div>

      {/* ── Object store ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 border-t border-[var(--border)] pt-3" data-testid="platform-storage">
        <div className="flex items-center gap-2">
          <h3 className="flex-1 text-[13px] font-medium">Platform object store</h3>
          <Button
            variant="ghost"
            size="sm"
            data-testid="test-platform-storage"
            disabled={storageTest.isPending}
            onClick={() => storageTest.mutate()}
          >
            {storageTest.isPending ? <Loader2 className="animate-spin" /> : null}
            Test connection
          </Button>
        </div>
        <p className="text-[12px] vt-muted" data-testid="platform-storage-summary">
          {data.status.storage.summary}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="s3.bucket">Bucket</Label>
            <Input
              id="s3.bucket"
              data-testid="platform-s3-bucket"
              value={edits['s3.bucket'] ?? data.s3.bucket ?? ''}
              onChange={(e) => setEdits((v) => ({ ...v, 's3.bucket': e.target.value }))}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="s3.region">Region</Label>
            <Input
              id="s3.region"
              data-testid="platform-s3-region"
              value={edits['s3.region'] ?? data.s3.region ?? ''}
              onChange={(e) => setEdits((v) => ({ ...v, 's3.region': e.target.value }))}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {secretRow('s3.accessKeyId', 'Access key ID', data.s3.accessKeyId)}
          {secretRow('s3.secretAccessKey', 'Secret access key', data.s3.secretAccessKey)}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="s3.endpoint">
            Endpoint <span className="font-normal vt-muted">— optional, for S3-compatible</span>
          </Label>
          <Input
            id="s3.endpoint"
            data-testid="platform-s3-endpoint"
            value={edits['s3.endpoint'] ?? data.s3.endpoint ?? ''}
            onChange={(e) => setEdits((v) => ({ ...v, 's3.endpoint': e.target.value }))}
          />
        </div>

        {storageResult && (
          <ul className="flex flex-col gap-0.5 rounded-[8px] border border-[var(--border)] p-2.5" data-testid="platform-storage-result">
            {/* Which credentials the run below actually used. First line,
                before any step, because it changes what every step means. */}
            <li className="mb-1 flex items-center gap-2 text-[12px]">
              <Badge tone={storageResult.credentialSource === 'platform' ? 'accent' : 'warn'}>
                <span data-testid="platform-storage-source">
                  {SOURCE_LABEL[storageResult.credentialSource]}
                </span>
              </Badge>
              <span className="vt-muted" data-testid="platform-storage-source-summary">
                {storageResult.credentialSummary}
              </span>
            </li>
            {storageResult.steps.map((step) => (
              <li key={step.step} className="flex items-start gap-1.5 text-[12px]">
                {step.ok ? (
                  <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--accent)]" />
                ) : (
                  <X className="mt-0.5 size-3.5 shrink-0 text-[var(--danger)]" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{step.step}</span>{' '}
                  <span className="vt-muted">
                    {step.detail}
                    {step.error ? ` — ${step.error}` : ''}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Web push ─────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 border-t border-[var(--border)] pt-3" data-testid="platform-push">
        <div className="flex items-center gap-2">
          <h3 className="flex-1 text-[13px] font-medium">Browser push (VAPID)</h3>
          <Badge tone={data.push.configured ? 'accent' : 'warn'}>
            {data.push.configured ? 'configured' : 'not configured'}
          </Badge>
        </div>
        <p className="text-[12px] vt-muted">
          A key pair signs every push notification. Generated here so the private half never
          leaves the server — and so nobody has to run a CLI to get one.
        </p>
        {/* V6-01 §2: rotation is not a maintenance action, it is an outage for
            everybody already subscribed. Saying so only in the confirm dialog
            means it is read after the decision rather than before it. */}
        <p className="text-[12px] text-[var(--warn)]">
          Regenerating invalidates every existing subscription. Each person has to click
          “Enable on this device” again, on every browser, before push reaches them.
        </p>
        {data.push.vapidPublicKey && (
          <code className="truncate rounded-[6px] bg-[var(--surface-2)] p-1.5 text-[11px]" data-testid="vapid-public-key">
            {data.push.vapidPublicKey}
          </code>
        )}
        <Button
          variant="subtle"
          size="sm"
          className="self-start"
          data-testid="generate-vapid"
          disabled={generateKeys.isPending}
          onClick={() => {
            if (data.push.configured && !window.confirm(
              'Replacing the keys unsubscribes every browser that is already subscribed. Continue?',
            )) return;
            generateKeys.mutate();
          }}
        >
          {generateKeys.isPending ? <Loader2 className="animate-spin" /> : <KeyRound />}
          {data.push.configured ? 'Regenerate keys' : 'Generate keys'}
        </Button>
      </div>

      {/* ── WhatsApp: one account, all workspaces (V5-06) ────────── */}
      <div className="flex flex-col gap-2 border-t border-[var(--border)] pt-3" data-testid="platform-whatsapp-test">
        <div className="flex items-center gap-2">
          <h3 className="flex-1 text-[13px] font-medium">Send a test WhatsApp</h3>
        </div>
        <span className="text-[11px] vt-muted">
          Sends the <span className="vt-mono">vt_verify</span> template from this account, down the
          same path a real notification takes. Full international form, no spaces — see{' '}
          <span className="vt-mono">docs/WHATSAPP_SETUP.md</span>.
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="w-52"
            aria-label="Test WhatsApp number"
            data-testid="whatsapp-test-number"
            placeholder="+919876543210"
            value={waTestNumber}
            onChange={(e) => setWaTestNumber(e.target.value)}
          />
          <Button
            variant="ghost"
            size="sm"
            data-testid="send-whatsapp-test"
            disabled={whatsappTest.isPending || !waTestNumber.trim()}
            onClick={() => whatsappTest.mutate(waTestNumber.trim())}
          >
            {whatsappTest.isPending ? <Loader2 className="animate-spin" /> : null}
            Send test WhatsApp
          </Button>
        </div>
        {waTestResult && (
          <p
            className="text-[12px]"
            data-testid="whatsapp-test-result"
            data-outcome={waTestResult.ok ? 'sent' : 'failed'}
            style={{ color: waTestResult.ok ? 'var(--accent)' : 'var(--danger)' }}
          >
            {waTestResult.message}
          </p>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
        <Button
          variant="primary"
          size="sm"
          data-testid="save-platform-settings"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <Loader2 className="animate-spin" /> : <Check />}
          Save credentials
        </Button>

        <Input
          aria-label="Test email recipient"
          data-testid="platform-test-to"
          className="w-[220px]"
          placeholder="you@example.com"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
        />
        <Button
          variant="subtle"
          size="sm"
          data-testid="platform-send-test"
          disabled={testing || !testTo.trim()}
          onClick={() => void sendTest()}
        >
          {testing ? <Loader2 className="animate-spin" /> : <Send />}
          Send test
        </Button>
      </div>
    </section>
  );
}
